import {getConfig} from '@/api/config'
import {ToolkitsActive} from '@/models/Config'
import {useGeneralStore} from '@/store/pinia/generalStore'
import {getToolkitsFromLocalStorage, isAlwaysAccessiblePage, isSuperuser} from '@/auth/ToolkitHelpers'

/**
 * Tries to get toolkits from pinia store
 * @returns {ToolkitsActive | null} Toolkits active configuration or null if not available
 */
function getToolkitsFromPinia (): ToolkitsActive | null {
  try {
    const generalStore = useGeneralStore()
    return generalStore.toolkitsActive
  } catch {
    // If pinia store is not available or fails, return null, we don't want to throw an error here
    return null
  }
}
/**
 * Fetches toolkits from /config endpoint
 * @returns {Promise<ToolkitsActive | null>} Promise that resolves to toolkits active configuration or null if not available
 */
async function fetchToolkitsFromEndpoint (): Promise<ToolkitsActive | null> {
  try {
    const response = await getConfig()
    const toolkits = response.data?.org_settings?.[0]?.toolkits_active || null

    // Store the config in localStorage for future use
    if (response.data) {
      try {
        const existingConfig = JSON.parse(localStorage.getItem('appConfig') || '{}')
        const updatedConfig = { ...existingConfig, ...response.data }
        localStorage.setItem('appConfig', JSON.stringify(updatedConfig))
      } catch (error) {
        console.warn('Failed to update appConfig in localStorage:', error)
      }
    }

    return toolkits
  } catch (error) {
    console.warn('Failed to fetch toolkits from endpoint:', error)
    return null
  }
}


/**
 * Gets toolkits active configuration, trying pinia first, then localStorage, then endpoint
 * @returns {Promise<ToolkitsActive>} Promise that resolves to toolkits active configuration
 */
export async function getToolkits (): Promise<ToolkitsActive> {
  // Try to get toolkits from pinia store
  const piniaToolkits = getToolkitsFromPinia()
  if (piniaToolkits) {
    return piniaToolkits
  }

  // If pinia fails, try to get toolkits from localStorage
  const localStorageToolkits = getToolkitsFromLocalStorage()
  if (localStorageToolkits) {
    return localStorageToolkits
  }

  // If localStorage fails, fetch toolkits from endpoint
  const endpointToolkits = await fetchToolkitsFromEndpoint()
  return endpointToolkits || {}
}

/**
 * Checks if a module is accessible for the current user
 * @param {string} module - The module name
 * @returns {Promise<boolean>} Promise that resolves to true if the module is accessible
 */
export async function canAccessModule (module: string): Promise<boolean> {
  // Superusers can access all modules
  if (isSuperuser()) {
    return true
  }

  // Try to get toolkits
  const toolkits = await getToolkits()

  // Check if the module is in the toolkits_active object
  return module in toolkits && (toolkits[module].length > 0)
}


/**
 * Checks if a page is accessible within a module for the current user
 * @param {string} module - The module name
 * @param {string} page - The page name
 * @returns {Promise<boolean>} Promise that resolves to true if the page is accessible
 */
export async function canAccessPage (module: string, page: string): Promise<boolean> {
  // Superusers can access all pages
  if (isSuperuser()) {
    return true
  }

  // Always accessible pages
  if (isAlwaysAccessiblePage(module, page)) {
    return true
  }

  // Try to get toolkits
  const toolkits = await getToolkits()

  // Check if the page is in the module's pages array
  const modulePages = toolkits[module] || []
  return modulePages.includes(page)
}
